/**
 * SlabReality Homepage - Dynamic CMS Version
 * ==========================================
 * ALL CONTENT LOADED FROM SUPABASE via Blueprint CMS
 * 
 * Sections:
 * 1. HERO - Tagline, title, description, CTAs, image, search, badges
 * 2. FEATURES - 6 feature cards with images
 * 3. JOURNAL - Recent posts from blueprint_posts
 * 4. SRE - Showcase section
 * 5. STATS - 4 KPI cards
 * 
 * Header and Footer remain unchanged (global components)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import supabase from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, Camera, ArrowRight, CheckCircle, Users, Cpu,
  Loader2, ChevronRight, Play,
  // Dynamic icons
  Layers, Globe, FolderKanban, Trophy, BadgeCheck, GitCompare,
  Box, Sparkles, Eye, Building2
} from 'lucide-react';

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Search, Camera, ArrowRight, CheckCircle, Users, Cpu, Layers, Globe,
  FolderKanban, Trophy, BadgeCheck, GitCompare, Box, Sparkles, Eye, Building2,
  // Add more as needed
};

const getIcon = (iconName) => ICON_MAP[iconName] || Sparkles;

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = ({ data, stoneTypes }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  if (!data) return null;

  // Parse title with italic support (*text* -> <em>text</em>)
  const parseTitle = (title) => {
    if (!title) return null;
    const parts = title.split(/(\*[^*]+\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={i} className="text-[#B08B5C] not-italic">{part.slice(1, -1)}</em>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/materials?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const trustBadges = data.trust_badges || [];

  return (
    <section className="relative min-h-[90vh] flex flex-col">
      {/* Background Image */}
      {data.hero_image_url && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${data.hero_image_url})` }}
        />
      )}
      
      {/* Dark Overlay - Concentrated on left, fades quickly */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] from-30% via-[#0a0a0a]/50 via-50% to-transparent to-70%" />
      
      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-12 flex-1 flex items-center py-20">
          {/* Left aligned content box - aligned with search form */}
          <div className="w-full max-w-xl space-y-6">
            {/* Tagline */}
            <p className="text-[#B08B5C] text-sm tracking-[0.2em] uppercase font-medium">
              {data.tagline_it}
            </p>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-[1.1] text-white">
              {parseTitle(data.title_it)}
            </h1>

            {/* Description */}
            <p className="text-white/70 text-lg leading-relaxed">
              {data.description_it}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 pt-2">
              <Button
                onClick={() => navigate(data.cta_primary_url || '/materials')}
                className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black px-8 py-6 text-base rounded-md font-medium"
              >
                {data.cta_primary_label_it}
              </Button>
              <Button
                onClick={() => navigate(data.cta_secondary_url || '/visual-search')}
                variant="outline"
                className="border-white/40 text-white hover:bg-white/10 px-8 py-6 text-base rounded-md"
              >
                {data.cta_secondary_label_it}
              </Button>
            </div>

            {/* Trust Badges */}
            {trustBadges.length > 0 && (
              <div className="flex flex-wrap gap-6 pt-4">
                {trustBadges.map((badge, i) => {
                  const Icon = getIcon(badge.icon);
                  return (
                    <div key={i} className="flex items-center gap-2 text-white/60">
                      <Icon className="w-4 h-4 text-[#B08B5C]" />
                      <span className="text-sm">{badge.label_it}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Search Bar - Bottom of hero */}
        <div className="relative z-10 pb-12">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="bg-[#1a1a18]/90 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <p className="text-white text-center mb-4 font-medium">
                Trova il materiale perfetto per il tuo progetto
              </p>
              
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={data.search_placeholder_it || 'Cerca per materiale, colore, provenienza...'}
                    className="w-full pl-12 py-6 bg-[#0a0a0a]/50 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => navigate('/visual-search')}
                  className="bg-[#2a2a28] hover:bg-[#3a3a38] text-white px-6 py-6 rounded-xl border border-white/10"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  {data.search_cta_label_it || 'Ricerca visiva'}
                </Button>
              </form>

              {/* Stone Type Pills */}
              {stoneTypes.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {stoneTypes.slice(0, 6).map((type) => (
                    <Link
                      key={type}
                      to={`/materials?stone_type=${encodeURIComponent(type)}`}
                      className="px-4 py-2 bg-[#0a0a0a]/50 hover:bg-[#2a2a28] text-white/70 hover:text-white rounded-full text-sm border border-white/10 transition-colors"
                    >
                      {type}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FEATURES SECTION
// ============================================================================
const FeaturesSection = ({ features }) => {
  if (!features || features.length === 0) return null;

  return (
    <section className="bg-[#1a1a18] py-16">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Fixed Grid - 6 columns on desktop, 3 on tablet, 2 on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {features.filter(f => f.is_active).map((feature) => {
            const Icon = getIcon(feature.icon);
            return (
              <Link
                key={feature.id}
                to={feature.link_url || '#'}
                className="group"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4 bg-[#0a0a0a]">
                  {feature.image_url ? (
                    <img
                      src={feature.image_url}
                      alt={feature.title_it}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-12 h-12 text-white/10" />
                    </div>
                  )}
                  
                  {/* Icon Badge */}
                  <div className="absolute bottom-3 left-3 w-10 h-10 bg-[#0a0a0a]/80 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/10">
                    <Icon className="w-5 h-5 text-[#B08B5C]" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-white font-semibold mb-1 group-hover:text-[#B08B5C] transition-colors text-sm lg:text-base">
                  {feature.title_it}
                </h3>
                <p className="text-white/50 text-xs lg:text-sm leading-relaxed mb-2 line-clamp-2">
                  {feature.description_it}
                </p>
                <span className="text-[#B08B5C] text-xs lg:text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                  {feature.link_label_it || 'Scopri di più'}
                  <ArrowRight className="w-3 h-3 lg:w-4 lg:h-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// JOURNAL SECTION
// ============================================================================
const JournalSection = ({ config, posts }) => {
  if (!config) return null;

  // Show section even without posts (with message)
  const hasPosts = posts && posts.length > 0;

  return (
    <section className="bg-[#0a0a0a] py-20 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex items-start justify-between mb-10">
          <div>
            <p className="text-[#B08B5C] text-sm tracking-wider uppercase mb-2">
              {config.label_it}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-3">
              {config.title_it}
            </h2>
            <p className="text-white/50 max-w-xl">
              {config.subtitle_it}
            </p>
          </div>
          <Link
            to={config.view_all_url || '/inspiration'}
            className="hidden md:flex items-center gap-2 text-[#B08B5C] hover:text-[#d4a872] transition-colors"
          >
            {config.view_all_label_it}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Posts Grid */}
        {hasPosts ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/inspiration/${post.slug}`}
                className="group"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4 bg-[#1a1a18]">
                  {post.cover_image_url ? (
                    <img
                      src={post.cover_image_url}
                      alt={post.title_it || post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles className="w-10 h-10 text-white/10" />
                    </div>
                  )}
                  
                  {/* Category Badge */}
                  {post.category && (
                    <div className="absolute top-3 left-3 px-3 py-1 bg-[#B08B5C] text-black text-xs font-medium rounded-full uppercase tracking-wider">
                      {post.category.name_it || post.category.name}
                    </div>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-white font-semibold mb-2 group-hover:text-[#B08B5C] transition-colors line-clamp-2">
                  {post.title_it || post.title}
                </h3>
                <p className="text-white/40 text-sm">
                  {post.reading_time || '5'} min di lettura
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#1a1a18] rounded-xl border border-white/5">
            <Sparkles className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/40">Nessun articolo pubblicato</p>
            <Link 
              to="/blueprint" 
              className="text-[#B08B5C] text-sm hover:underline mt-2 inline-block"
            >
              Crea il primo articolo →
            </Link>
          </div>
        )}

        {/* Mobile View All Link */}
        <div className="md:hidden mt-8 text-center">
          <Link
            to={config.view_all_url || '/inspiration'}
            className="inline-flex items-center gap-2 text-[#B08B5C]"
          >
            {config.view_all_label_it}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SRE SECTION
// ============================================================================
const SRESection = ({ data }) => {
  const navigate = useNavigate();
  
  if (!data) return null;

  return (
    <section className="bg-[#121212] py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="space-y-6">
            <p className="text-[#B08B5C] text-sm tracking-wider uppercase">
              {data.label_it}
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight">
              {data.title_it}
            </h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              {data.description_it}
            </p>
            <Button
              onClick={() => navigate(data.cta_url || '/sre')}
              className="bg-[#B08B5C] hover:bg-[#9a7a4f] text-black px-8 py-6 text-base rounded-full"
            >
              {data.cta_label_it}
            </Button>
          </div>

          {/* Right - Image */}
          <div className="relative">
            {data.image_url ? (
              <div className="relative rounded-2xl overflow-hidden aspect-video">
                <img
                  src={data.image_url}
                  alt="SRE Preview"
                  className="w-full h-full object-cover"
                />
                {/* SRE Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-[#B08B5C] text-black text-xs font-bold rounded">
                  SRE
                </div>
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-[#1a1a18] rounded-2xl flex items-center justify-center">
                <Box className="w-16 h-16 text-white/10" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// STATS SECTION
// ============================================================================
const StatsSection = ({ stats }) => {
  if (!stats || stats.length === 0) return null;

  return (
    <section className="bg-[#0a0a0a] py-16 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.filter(s => s.is_active).map((stat) => {
            const Icon = getIcon(stat.icon);
            return (
              <div key={stat.id} className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 bg-[#1a1a18] rounded-full flex items-center justify-center border border-white/10">
                    <Icon className="w-5 h-5 text-[#B08B5C]" />
                  </div>
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-white/50 text-sm">
                  {stat.label_it}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================
const Landing = () => {
  const [loading, setLoading] = useState(true);
  const [heroData, setHeroData] = useState(null);
  const [featuresData, setFeaturesData] = useState([]);
  const [journalConfig, setJournalConfig] = useState(null);
  const [journalPosts, setJournalPosts] = useState([]);
  const [sreData, setSreData] = useState(null);
  const [statsData, setStatsData] = useState([]);
  const [stoneTypes, setStoneTypes] = useState([]);

  // Load all data from Supabase
  const loadData = useCallback(async () => {
    try {
      // Load Hero
      const { data: hero } = await supabase
        .from('blueprint_homepage_hero')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .single();
      setHeroData(hero);

      // Load Features
      const { data: features } = await supabase
        .from('blueprint_homepage_features')
        .select('*')
        .eq('is_active', true)
        .order('order_index');
      setFeaturesData(features || []);

      // Load Journal Config
      const { data: journal } = await supabase
        .from('blueprint_homepage_journal')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .single();
      setJournalConfig(journal);

      // Load Recent Posts for Journal with category name
      if (journal) {
        const { data: posts } = await supabase
          .from('blueprint_posts')
          .select(`
            id, slug, title, title_it, cover_image_url, reading_time, published_at,
            category:blueprint_post_categories(name_it, name, slug)
          `)
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .limit(journal.posts_count || 4);
        setJournalPosts(posts || []);
      }

      // Load SRE Section
      const { data: sre } = await supabase
        .from('blueprint_homepage_sre')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .single();
      setSreData(sre);

      // Load Stats
      const { data: stats } = await supabase
        .from('blueprint_homepage_stats')
        .select('*')
        .eq('is_active', true)
        .order('order_index');
      setStatsData(stats || []);

      // Load Categories from Supabase materials table
      try {
        const { data: materialsData, error: matError } = await supabase
          .from('materials')
          .select('category');
        
        if (!matError && materialsData) {
          // Extract unique categories (category field in Supabase, not stone_type)
          const categories = [...new Set(
            materialsData
              .map(m => m.category)
              .filter(Boolean)
          )];
          setStoneTypes(categories.sort());
        }
      } catch (err) {
        console.error('Error loading categories from Supabase:', err);
      }

    } catch (err) {
      console.error('Error loading homepage data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      <main className="pt-20">
        <HeroSection data={heroData} stoneTypes={stoneTypes} />
        <FeaturesSection features={featuresData} />
        <JournalSection config={journalConfig} posts={journalPosts} />
        <SRESection data={sreData} />
        <StatsSection stats={statsData} />
      </main>

      <Footer />
    </div>
  );
};

export default Landing;
